import { useAuth } from "@clerk/vue";
import { useToast } from "@/composables/useToast";

export type ApiOptions = RequestInit & {
  path?: string;
  baseUrl?: string;
  suppressErrorToast?: boolean; // Don't show toast on errors
};

// Module-level CSRF token storage for cross-domain compatibility
// In cross-domain deployments, document.cookie can't read cookies set by backend
let csrfToken: string | null = null;

export function setCSRFToken(token: string | null): void {
  csrfToken = token;
}

export function getCSRFToken(): string | null {
  return csrfToken;
}

export function useApi() {
  const { getToken, isSignedIn } = useAuth();
  const { error: toastError } = useToast();

  async function retryWithBackoff<T>(
    operation: () => Promise<T>,
    shouldRetry: (e: unknown) => { retry: boolean; delayMs?: number },
    maxAttempts: number = 3,
    baseDelayMs: number = 250,
  ): Promise<T> {
    let attempt = 0;
    let lastError: unknown = null;
    while (attempt < maxAttempts) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        const decision = shouldRetry(error);
        if (!decision.retry || attempt === maxAttempts - 1) break;
        const jitter = Math.floor(Math.random() * 100);
        const backoff = decision.delayMs ?? baseDelayMs * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, backoff + jitter));
        attempt += 1;
      }
    }
    throw lastError instanceof Error ? lastError : new Error("Request failed");
  }

  async function apiFetch(input: string | URL, init?: ApiOptions) {
    // Get auth token from Clerk
    const tokenFn = getToken.value;
    const token = typeof tokenFn === "function" ? await tokenFn() : null;
    const suppressErrorToast = init?.suppressErrorToast ?? false;
    const rawUrl =
      typeof input === "string" || input instanceof URL ? input : String(input);
    const envBase = (import.meta.env.VITE_API_URL as string | undefined)
      ?.toString()
      .trim();
    const fallback =
      typeof window !== "undefined" && window.location.port === "5173"
        ? "http://localhost:8080"
        : typeof window !== "undefined"
          ? window.location.origin
          : "";
    const base = envBase && envBase.length > 0 ? envBase : fallback;
    const url =
      typeof rawUrl === "string" && rawUrl.startsWith("/")
        ? `${base}${rawUrl}`
        : rawUrl;

    const headers: HeadersInit = { ...(init?.headers ?? {}) };

    // Attach CSRF header from stored token (cross-domain compatible)
    // Token is fetched via /api/csrf-token endpoint after auth
    if (csrfToken) {
      (headers as Record<string, string>)["x-csrf-token"] = csrfToken;
    }

    if (isSignedIn.value && token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }

    // Include cookies for CSRF double-submit cookie pattern and cross-origin auth
    let response: Response;
    try {
      response = await retryWithBackoff(
        () =>
          fetch(url as string | URL, {
            credentials: "include",
            ...init,
            headers,
          }),
        (e: unknown) => {
          // Retry network or CORS errors (fetch throws TypeError)
          if (e instanceof TypeError) {
            return { retry: true };
          }
          return { retry: false };
        },
      );
    } catch (e) {
      // Network error before we have a Response
      if (!suppressErrorToast) {
        toastError(
          "Network Error",
          "We couldn't reach the server. Check your connection and try again.",
        );
      }
      throw e;
    }

    // Retry transient HTTP statuses (single retry with backoff, then give up)
    const transientStatuses = new Set([408, 429, 502, 503, 504]);
    if (transientStatuses.has(response.status)) {
      const retryAfterHeader = response.headers.get("retry-after");
      const retryAfterSeconds = retryAfterHeader
        ? parseInt(retryAfterHeader, 10)
        : undefined;
      const delayMs = Number.isFinite(retryAfterSeconds)
        ? Math.max(0, (retryAfterSeconds as number) * 1000)
        : 400;
      await new Promise((r) => setTimeout(r, delayMs));
      response = await fetch(url as string | URL, {
        credentials: "include",
        ...init,
        headers,
      });
    }

    // If unauthorized, try once to refresh token and retry
    if (response.status === 401 && isSignedIn.value) {
      const tokenFn = getToken.value;
      const freshToken = typeof tokenFn === "function" ? await tokenFn() : null;
      if (freshToken) {
        (headers as Record<string, string>)["Authorization"] =
          `Bearer ${freshToken}`;
        response = await fetch(url as string | URL, {
          credentials: "include",
          ...init,
          headers,
        });
      }
      // If still unauthorized, emit a session-expired event and redirect to login
      if (response.status === 401) {
        const pathname =
          typeof window !== "undefined"
            ? window.location.pathname + window.location.search
            : "/";
        let notCancelled = true;
        try {
          const event = new CustomEvent("session-expired", {
            cancelable: true,
            detail: { redirectPath: pathname },
          } as CustomEventInit);
          notCancelled =
            globalThis.dispatchEvent?.(event as unknown as Event) ?? true;
        } catch {
          // ignore
        }
        if (notCancelled) {
          const redirectUrl = `/login?redirect=${encodeURIComponent(pathname)}`;
          if (
            typeof window !== "undefined" &&
            typeof window.open === "function"
          ) {
            window.open(redirectUrl, "_self");
          }
        }
      }
    }

    const contentType = response.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");
    if (!response.ok) {
      const body = isJson
        ? await response.json().catch(() => ({}))
        : await response.text().catch(() => "");

      // Show user-friendly error toast
      let errorMessage = "An error occurred";
      // Handle common statuses
      if (response.status === 404) {
        errorMessage = "The requested resource was not found";
      } else if (response.status === 403) {
        // Check for billing soft-lock (Tech-Spec: Stripe Billing, Task 26)
        const errorCode =
          isJson && typeof body === "object" && body
            ? (body as { error?: { code?: string } }).error?.code
            : undefined;
        if (errorCode === "BILLING_REQUIRED") {
          errorMessage =
            "Your account is in read-only mode. Please set up billing to continue.";
        } else {
          errorMessage = "You don't have permission to perform this action";
        }
      } else if (response.status === 408) {
        errorMessage = "The request timed out. Please try again.";
      } else if (response.status === 429) {
        const retryAfter = response.headers.get("retry-after");
        const seconds = retryAfter ? parseInt(retryAfter, 10) : undefined;
        errorMessage = seconds
          ? `You're sending requests too quickly. Try again in ${seconds} seconds.`
          : "You're sending requests too quickly. Please wait a moment and try again.";
      } else if (response.status === 500) {
        errorMessage = "A server error occurred. Please try again later";
      } else if (
        response.status === 502 ||
        response.status === 503 ||
        response.status === 504
      ) {
        errorMessage =
          "The server is temporarily unavailable. Please try again shortly.";
      } else if (
        isJson &&
        typeof body === "object" &&
        body &&
        "message" in body
      ) {
        errorMessage = (body as { message?: string }).message || errorMessage;
      }

      if (!suppressErrorToast) {
        toastError("Request Failed", errorMessage);
      }

      // Attach structured info so callers can branch on the status/body of a
      // failed request (apiFetch throws on any non-2xx rather than returning it).
      const apiError = new Error(
        `[api] ${response.status} ${response.statusText} ${isJson ? JSON.stringify(body) : body}`,
      ) as Error & { status?: number; body?: unknown };
      apiError.status = response.status;
      apiError.body = body;
      throw apiError;
    }
    return response;
  }

  return { apiFetch };
}

/**
 * Fetch CSRF token from the backend API.
 * This is needed for cross-domain deployments where the frontend can't read
 * cookies set by the backend domain.
 *
 * Call this after authentication to enable CSRF-protected requests.
 * @param authToken - Optional Bearer token for authentication
 */
export async function fetchCSRFToken(
  authToken?: string,
): Promise<string | null> {
  try {
    // Build URL the same way apiFetch does
    const envBase = (import.meta.env.VITE_API_URL as string | undefined)
      ?.toString()
      .trim();
    const fallback =
      typeof window !== "undefined" && window.location.port === "5173"
        ? "http://localhost:8080"
        : typeof window !== "undefined"
          ? window.location.origin
          : "";
    const base = envBase && envBase.length > 0 ? envBase : fallback;
    const url = `${base}/api/csrf-token`;

    const headers: HeadersInit = {};
    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers,
    });

    if (!response.ok) {
      console.warn(
        "[fetchCSRFToken] Failed to fetch CSRF token:",
        response.status,
      );
      return null;
    }

    const data = await response.json();
    if (data.csrfToken) {
      csrfToken = data.csrfToken;
      return csrfToken;
    }
  } catch (err) {
    console.warn("[fetchCSRFToken] Error fetching CSRF token:", err);
  }
  return null;
}
